import { RealtimeModes as modes } from "mobility-toolbox-js/api";
import Feature from "ol/Feature";
import OLMap from "ol/Map";
import PropTypes from "prop-types";
import { Component, createRef } from "react";
import { connect } from "react-redux";
import Copyright from "react-spatial/components/Copyright";

import {
  getLines,
  setIsFollowingVehicle,
  setMode,
  setSelectedVehicles,
  subscribeDeletedVehicles,
  subscribeDepartures,
  subscribeDisruptions,
  subscribeExtraLayerTags,
  subscribeGeofoxDepartures,
  subscribeStopSequence,
  unsubscribeDepartures,
  unsubscribeGeofoxDepartures,
  unsubscribeStopSequence,
  updateStation,
} from "../model/actions";
import tralisApi from "../TralisAPI";
import TralisLayer from "../TralisLayer";
import { getRandomVehicle } from "../utils";
import isVehicleToHighlight from "../utils/isVehicleToHighlight";

import BookmarksPopup from "./BookmarksPopup";
import MapControls from "./controls/MapControls";
import ControlsHiddenListener from "./ControlsHiddenListener";
import DebugModeListener from "./DebugModeListener";
import PlatformInfo from "./departure/PlatformInfo";
import Departures from "./Departures";
import HealthInfo from "./HealthInfo";
import LegalLinks from "./LegalLinks";
import LiveMap from "./LiveMap";
import MotsRelations from "./MotsRelation";
import Overlay from "./Overlay";
import PlatformInfoFocusListener from "./PlatformInfoFocusListener";
import RisPopup from "./RisPopup";
import RouteInfo from "./RouteInfo";
import Situations from "./Situations";
import StationIconsLegendPopup from "./StationIconsLegendPopup";
import VehiclesList from "./VehiclesList";
import WarningInfo from "./WarningInfo";
import WebsocketInfo from "./WebsocketInfo";

import "./Navigator.scss";

const propTypes = {
  activeNotifications: PropTypes.arrayOf(PropTypes.object),
  areSituationsOpen: PropTypes.bool,
  auto: PropTypes.bool,
  config: PropTypes.object.isRequired,
  departures: PropTypes.arrayOf(PropTypes.object).isRequired,
  departuresLoading: PropTypes.bool.isRequired,
  dispatchGetLines: PropTypes.func.isRequired,
  dispatchSetIsFollowingVehicle: PropTypes.func.isRequired,
  dispatchSetMode: PropTypes.func.isRequired,
  dispatchSetSelectedVehicles: PropTypes.func.isRequired,
  dispatchSubscribeDeletedVehicles: PropTypes.func.isRequired,
  dispatchSubscribeDepartures: PropTypes.func.isRequired,
  dispatchSubscribeDisruptions: PropTypes.func.isRequired,
  dispatchSubscribeExtraLayerTags: PropTypes.func.isRequired,
  dispatchSubscribeGeofoxDepartures: PropTypes.func.isRequired,
  dispatchSubscribeStopSequence: PropTypes.func.isRequired,
  dispatchUnsubscribeDepartures: PropTypes.func.isRequired,
  dispatchUnsubscribeGeofoxDepartures: PropTypes.func.isRequired,
  dispatchUnsubscribeStopSequence: PropTypes.func.isRequired,
  dispatchUpdateStation: PropTypes.func.isRequired,
  hideOthers: PropTypes.bool,
  history: PropTypes.object,
  incidentProgram: PropTypes.bool,
  isMotsRelationOpened: PropTypes.bool.isRequired,
  isRouteInfoOpened: PropTypes.bool.isRequired,
  map: PropTypes.instanceOf(OLMap).isRequired,
  mode: PropTypes.string,
  realtimeLayer: PropTypes.instanceOf(TralisLayer),
  selectedVehicles: PropTypes.arrayOf(PropTypes.object),
  situations: PropTypes.arrayOf(PropTypes.object),
  station: PropTypes.instanceOf(Feature),
  stationId: PropTypes.string,
  train: PropTypes.string,
  trainAuto: PropTypes.bool,
};

const defaultProps = {
  auto: false,
  hideOthers: null,
  history: null,
  incidentProgram: null,
  mode: modes.SCHEMATIC,
  realtimeLayer: null,
  selectedVehicles: null,
  station: null,
  train: null,
  trainAuto: false,
};

class Navigator extends Component {
  constructor(props) {
    super(props);
    const { dispatchUpdateStation, mode, stationId } = this.props;

    this.state = {
      isBottomContainerActive: true,
      shouldControlsHidden: false,
    };

    this.bookmarkListRef = createRef();
    this.platformInfoRef = createRef();

    const searchParams = new URLSearchParams(window.location.search);
    this.sortByMinArrivalTime = searchParams.get("sort") === "mfz";
    this.simulateIncident = searchParams.get("incident") === "true";
    const showDepartures = searchParams.get("showDepartures") === "true";
    if (stationId) {
      dispatchUpdateStation(stationId, mode, showDepartures);
    }
  }

  componentDidMount() {
    const {
      config,
      dispatchGetLines,
      dispatchSubscribeDeletedVehicles,
      dispatchSubscribeDisruptions,
      dispatchSubscribeExtraLayerTags,
      incidentProgram,
    } = this.props;

    dispatchSubscribeDisruptions();
    if (config.lineParam) {
      dispatchGetLines();
    }
    dispatchSubscribeDeletedVehicles();
    dispatchSubscribeExtraLayerTags();

    tralisApi.maxDepartureAge =
      this.simulateIncident || incidentProgram ? 65 : 30;

    this.updateAutoSwitchMode();
    this.updateMapCenter();
  }

  componentDidUpdate(prevProps, prevState) {
    const {
      areSituationsOpen,
      departures,
      departuresLoading,
      incidentProgram,
      isRouteInfoOpened,
      mode,
      selectedVehicles,
      situations,
      station,
    } = this.props;
    const { isBottomContainerActive } = this.state;

    if (incidentProgram !== prevProps.incidentProgram) {
      tralisApi.maxDepartureAge =
        this.simulateIncident || incidentProgram ? 65 : 30;
    }

    // Update the auto switch between modes
    if (prevProps.mode !== mode || station !== prevProps.station) {
      this.updateAutoSwitchMode();
    }

    if (selectedVehicles !== prevProps.selectedVehicles) {
      this.updateSelectedVehicles(prevProps.selectedVehicles);
    }

    if (
      prevProps.station !== station &&
      !(
        station &&
        prevProps.station &&
        prevProps.station.get("name") === station.get("name")
      )
    ) {
      this.updateStationParam();
      this.updateStation(prevProps.station);
    }

    // Automatically display the bottom container if a new station or vehicle or s situation has been clicked.
    const hasDepartures = station && (!departuresLoading || departures.length);
    const hasSelectedVehicles = !!selectedVehicles?.length;
    const hasSituations = !!situations?.length;
    const shouldBottomContainerActive =
      (hasSituations && areSituationsOpen) ||
      hasDepartures ||
      (isRouteInfoOpened && hasSelectedVehicles);

    // Re-open the bottom container when the user selects an element on the map
    if (shouldBottomContainerActive !== prevState.isBottomContainerActive) {
      this.setState({
        isBottomContainerActive: shouldBottomContainerActive,
      });
    }

    // Update the map center if the bottom container state has changed.
    if (isBottomContainerActive !== prevState.isBottomContainerActive) {
      this.updateMapCenter();
    }
  }

  render() {
    const {
      areSituationsOpen,
      config,
      departures,
      history,
      isRouteInfoOpened,
      map,
      selectedVehicles,
      situations,
    } = this.props;
    const { isBottomContainerActive, mapStyle, shouldControlsHidden } =
      this.state;
    const classes = [];
    const hasRouteInfo = selectedVehicles.length;
    const showClosedPlatformInfo =
      config.bottomContainer &&
      config.platformInfo &&
      !isBottomContainerActive &&
      !areSituationsOpen &&
      !!situations?.length;
    const showPlatformInfo =
      config.bottomContainer &&
      config.platformInfo &&
      !isRouteInfoOpened &&
      !departures?.length &&
      !!situations?.length;

    if (showClosedPlatformInfo || showPlatformInfo) {
      classes.push("platform-info-active");
    }

    if (shouldControlsHidden) {
      classes.push("controls-hidden");
    }

    if (!areSituationsOpen) {
      classes.push("situations-closed");
    }

    if (config.navigatorClass) {
      classes.push(config.navigatorClass);
    }

    if (config.bottomContainer && isBottomContainerActive) {
      classes.push("bottom-container-active");
    }

    if (config.routeInfo && isRouteInfoOpened && hasRouteInfo) {
      classes.push("route-info-active");
    }

    if (config.mapUnderBottomContainer) {
      classes.push("map-under-bottom-container");
    }

    return (
      <div
        className={`${classes.join(" ")}`}
        id="navigator"
        style={{
          height: "100%",
          overflowY: "hidden",
          position: "relative",
          width: "100%",
        }}
      >
        <div className="trl-main-flex-container">
          <div>
            <LiveMap history={history} mapStyle={mapStyle} />
            {config.mapControls && <MapControls history={history} />}
            <div className="trl-bottom-links">
              {config.legalLinks && <LegalLinks />}
              <Copyright className="copyright" map={map} />
            </div>

            {/* Platforminfo must always be displayed */}
            {showClosedPlatformInfo && (
              <PlatformInfo
                className="platform-info-situations-closed"
                ref={this.platformInfoRef}
              />
            )}
          </div>

          {config.bottomContainer && isBottomContainerActive && (
            <Overlay id="bottom-container">
              <HealthInfo />
              {config.maskInfo && <WarningInfo />}
              {showPlatformInfo && <PlatformInfo ref={this.platformInfoRef} />}
              {config.routeInfo && <RouteInfo />}
              {config.departuresList && <Departures />}
              {/* {config.disruptionInfo && <DisruptionInfo />} */}
              {!isRouteInfoOpened &&
                !departures?.length &&
                !!areSituationsOpen && <Situations />}

              <WebsocketInfo />
            </Overlay>
          )}
        </div>

        {config.vehiclesList && <VehiclesList />}
        {config.risPopup && <RisPopup />}
        {config.bookmarks && <BookmarksPopup listRef={this.bookmarkListRef} />}
        {config.motsRelation && <MotsRelations />}
        {config.stationIconsLegendPopup && <StationIconsLegendPopup />}
        {/* Use <GlobalDialog /> component to display timed global notifications on load */}

        <DebugModeListener />
        <PlatformInfoFocusListener platformInfoRef={this.platformInfoRef} />
        <ControlsHiddenListener
          onChange={(hidden) => {
            this.setState({ shouldControlsHidden: hidden });
          }}
        />
      </div>
    );
  }

  // Update the automatic switch between mode when a auto mode is activated.
  updateAutoSwitchMode() {
    const { auto, dispatchSetMode, mode, station } = this.props;

    clearTimeout(this.updateModeTimeout);

    if (!auto) {
      return;
    }

    if (station) {
      // If a station is selected we switch to the schematic mode
      dispatchSetMode(modes.SCHEMATIC);
    }

    // If not we look if we can switch mode after 18sec.
    this.updateModeTimeout = setTimeout(() => {
      const { station: oldStation } = this.props;
      // We don't swicth mode if there is a station selected.
      dispatchSetMode(
        !oldStation && mode === modes.SCHEMATIC
          ? modes.TOPOGRAPHIC
          : modes.SCHEMATIC,
      );
    }, 28000);
  }

  // Update map's center when the bottom container is over the map
  updateMapCenter() {
    // @experimetal
    // On videowall mode, we want to see the all the map when the bottomContainer is active or not.
    const { config, map } = this.props;
    const { isBottomContainerActive } = this.state;
    const navigator = document.getElementById("navigator");
    const bottomContainer = document.getElementById("bottom-container");
    if (
      config.mapUnderBottomContainer &&
      map &&
      navigator &&
      bottomContainer &&
      navigator?.clientWidth > 1200 // only xl screen
    ) {
      map.getView().fit(config.schematic.dataExtent, {
        padding: [0, isBottomContainerActive ? 340 : 0, 0, 0],
      });
    }
  }

  // Update subscriptions for selected vehicle
  updateSelectedVehicles() {
    const {
      config,
      dispatchSetIsFollowingVehicle,
      dispatchSetSelectedVehicles,
      dispatchSubscribeStopSequence,
      dispatchUnsubscribeStopSequence,
      hideOthers,
      realtimeLayer,
      selectedVehicles,
      trainAuto,
    } = this.props;

    const [vehicle] = selectedVehicles;

    if (
      this.lastStopSeqSubscribedId &&
      (!vehicle || vehicle.train_id !== this.lastStopSeqSubscribedId)
    ) {
      dispatchUnsubscribeStopSequence(this.lastStopSeqSubscribedId);
      this.lastStopSeqSubscribedId = null;
    }

    if (
      selectedVehicles.length === 1 &&
      this.lastStopSeqSubscribedId !== vehicle.train_id &&
      // SBAHNMW-891, HACK:
      // Vehicle with no train number returns a stop sequence in the past,
      // so we exclude them from click selection.
      // This should be fix in backend.
      vehicle.train_number
    ) {
      dispatchSubscribeStopSequence(vehicle.train_id);
      this.lastStopSeqSubscribedId = vehicle.train_id;
    }
    // If trainAuto is true that means, we select automatically a new train after one disappear or is unselected.
    if (realtimeLayer && trainAuto && selectedVehicles.length === 0) {
      const vehicles =
        Object.values(realtimeLayer.trajectories || []).filter(
          config.findTrainAuto,
        ) || [];

      if (vehicles.length) {
        const newVehicle = getRandomVehicle(vehicles);
        tralisApi.unsubscribeTrajectory(this.onTrajectoryMessage);

        // Update the tracker filter.
        if (hideOthers) {
          realtimeLayer.filter = (feature) => {
            const vehi = feature.properties;
            return isVehicleToHighlight(vehi, newVehicle.train_id);
          };
        }
        dispatchSetSelectedVehicles([newVehicle]);
        dispatchSetIsFollowingVehicle(true);

        // isFollowing is set to false esomewhere , this ensure we set it back to true to follow the new vehicle
        setTimeout(() => {
          dispatchSetIsFollowingVehicle(true);
        }, 1000);
      }
    } else if (selectedVehicles.length === 0) {
      dispatchSetIsFollowingVehicle(false);
    }
  }

  // Update subscription for a selected station
  updateStation(prevStation) {
    const {
      dispatchSubscribeDepartures,
      dispatchSubscribeGeofoxDepartures,
      dispatchUnsubscribeDepartures,
      dispatchUnsubscribeGeofoxDepartures,
      station,
    } = this.props;

    const prevUic = prevStation?.get("uic");
    if (prevUic) {
      dispatchUnsubscribeDepartures(prevUic);
      dispatchUnsubscribeGeofoxDepartures(prevUic);
    }

    const uic = station?.get("uic");
    if (uic) {
      dispatchSubscribeDepartures(uic, this.sortByMinArrivalTime);
      dispatchSubscribeGeofoxDepartures(uic);
    }
  }

  updateStationParam() {
    const { config, history, station } = this.props;

    if (!config.stationParam) {
      return;
    }

    const urlSearch = window.location?.search;
    const urlPathname = window.location?.pathname;
    const isNavigator =
      config.navigatorParam && urlPathname.includes("navigator");

    const uic = station?.get("uic") || "";

    // Update url
    const path = isNavigator ? "/navigator" : "";
    const url = `${path}/${uic}${urlSearch || ""}`;
    history?.replace(url);
  }
}

Navigator.propTypes = propTypes;
Navigator.defaultProps = defaultProps;

const mapStateToProps = (state) => ({
  areSituationsOpen: state.areSituationsOpen,
  auto: state.auto,
  config: state.config,
  departures: state.departures,
  departuresLoading: state.departuresLoading,
  hideOthers: state.hideOthers,
  incidentProgram: state.incidentProgram,
  isMotsRelationOpened: state.isMotsRelationOpened,
  isRouteInfoOpened: state.isRouteInfoOpened,
  map: state.map,
  mode: state.mode,
  realtimeLayer: state.realtimeLayer,
  selectedVehicles: state.selectedVehicles,
  situations: state.situations,
  station: state.station,
  train: state.train,
  trainAuto: state.trainAuto,
});

const mapDispatchToProps = {
  dispatchGetLines: getLines,
  dispatchSetIsFollowingVehicle: setIsFollowingVehicle,
  dispatchSetMode: setMode,
  dispatchSetSelectedVehicles: setSelectedVehicles,
  dispatchSubscribeDeletedVehicles: subscribeDeletedVehicles,
  dispatchSubscribeDepartures: subscribeDepartures,
  dispatchSubscribeDisruptions: subscribeDisruptions,
  dispatchSubscribeExtraLayerTags: subscribeExtraLayerTags,
  dispatchSubscribeGeofoxDepartures: subscribeGeofoxDepartures,
  dispatchSubscribeStopSequence: subscribeStopSequence,
  dispatchUnsubscribeDepartures: unsubscribeDepartures,
  dispatchUnsubscribeGeofoxDepartures: unsubscribeGeofoxDepartures,
  dispatchUnsubscribeStopSequence: unsubscribeStopSequence,
  dispatchUpdateStation: updateStation,
};

export default connect(mapStateToProps, mapDispatchToProps)(Navigator);
